import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { INSIGHTS, INSIGHT_CATEGORIES, INSIGHT_CATEGORY, formatInsightDate, readTimeLabel } from "@/data/insights";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

const catId = (cat) => cat.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const GROUPS = INSIGHT_CATEGORIES.map((cat) => ({
  cat,
  items: INSIGHTS.filter((a) => INSIGHT_CATEGORY[a.slug] === cat),
})).filter((g) => g.items.length > 0);

export default function InsightsPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: "Insights & Perspectives | BLEZZINGS Growth Partners",
    description:
      "Perspectives on growth, pricing, commercial strategy and market leadership from BLEZZINGS Growth Partners.",
    canonical: "https://blezzings.co/insights",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Insights", url: "https://blezzings.co/insights" },
    ],
  });

  const openBook = (source) => {
    track("book_consultation_open", { source });
    setBookOpen(true);
  };

  return (
    <div data-testid="insights-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      {/* Header */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-[#fafafa]">
        <div className={GRID}>
          <SectionBadge label="The BLEZZINGS Perspective" />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-3xl">
            Ideas for leaders building the next phase of growth.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Long-form perspectives on the commercial questions that
            decide whether growth becomes durable — or merely expensive.
          </p>
          <div data-testid="insights-cat-nav" className="mt-10 flex flex-wrap gap-2">
            {GROUPS.map((g) => (
              <a
                key={g.cat}
                href={`#cat-${catId(g.cat)}`}
                data-testid={`insights-cat-link-${catId(g.cat)}`}
                className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 bg-white text-sm text-zinc-700 hover:border-black hover:text-black transition-colors duration-300"
              >
                {g.cat}
                <span className="text-xs text-zinc-400">{g.items.length}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Article index, grouped by commercial problem */}
      <section data-testid="insights-index" className="pb-20 md:pb-24 bg-[#fafafa]">
        <div className={GRID}>
          {GROUPS.map((g) => (
            <div
              key={g.cat}
              id={`cat-${catId(g.cat)}`}
              data-testid={`insights-cat-${catId(g.cat)}`}
              className="scroll-mt-28 mt-14 first:mt-0 pt-2"
            >
              <div className="flex items-baseline justify-between gap-4 border-b-2 border-black pb-4">
                <h2 className="font-serif-display text-2xl sm:text-3xl text-black">
                  {g.cat}
                </h2>
                <span className="overline whitespace-nowrap">
                  {g.items.length} {g.items.length === 1 ? "article" : "articles"}
                </span>
              </div>
              {g.items.map((a, i) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  data-testid={`insights-row-${a.slug}`}
                  onClick={() => track("insight_open", { slug: a.slug, source: "index" })}
                  className="group grid grid-cols-12 gap-4 md:gap-8 md:items-center py-8 md:py-10 border-b border-black/10"
                >
                  <div className="col-span-12 md:col-span-1">
                    <span className="overline">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  {a.image && (
                    <div className="col-span-12 md:col-span-3">
                      <div className="aspect-[16/10] overflow-hidden bg-zinc-200">
                        <img
                          src={a.image}
                          alt={a.imageAlt || a.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      </div>
                    </div>
                  )}
                  <div className={`col-span-12 ${a.image ? "md:col-span-5" : "md:col-span-8"}`}>
                    <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-tight text-black transition-colors duration-300 group-hover:text-zinc-700">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed max-w-xl">
                      {a.description}
                    </p>
                  </div>
                  <div className="col-span-10 md:col-span-2 text-sm text-zinc-500">
                    {formatInsightDate(a.date)} · {readTimeLabel(a)}
                  </div>
                  <div className="col-span-2 md:col-span-1 flex md:justify-end">
                    <ArrowUpRight className="h-5 w-5 text-zinc-300 transition-all duration-300 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </Link>
              ))}
            </div>
          ))}
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
